/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import moment from "moment";

const includeLeadingZero = (value) => {
  if (value < 10) {
    return "0" + value;
  }

  return value;
};

export const formatDate = (date) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  return `${year}-${includeLeadingZero(month)}-${includeLeadingZero(day)}`;
};

export const getRangeOfDates = (d1, d2 = d1) => {
  let startDate, endDate;
  if (d1 > d2) {
    startDate = d2;
    endDate = d1;
  } else {
    startDate = d1;
    endDate = d2;
  }

  const dates = [];
  let currentDate = startDate;
  while (currentDate <= endDate) {
    const date = formatDate(new Date(currentDate));
    dates.push(date);
    currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
  }

  return dates;
};

export const calculateDaysBetweenTwoDates = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const formatDateToMonthName = (date) => {
  const dateObj = new Date(date);
  const month = dateObj.toLocaleString("default", { month: "short" });
  const day = dateObj.getDate();
  return `${month} ${day}`;
};

export const formatDateToMonthNameAndYear = (date) => {
  const dateObj = new Date(date);
  const month = dateObj.toLocaleString("default", { month: "short" });
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  return `${month} ${day}, ${year}`;
};

export const getStringifiedDate = (date) => {
  const dateObj = new Date(date);
  return dateObj.toDateString();
  // Output: "Sun Sep 04 2022"
};

export const getTimeFromDate = (date) => {
  const dateObj = new Date(date);
  return moment(dateObj).format("hh:mm A");
};

export const compareDates = (date1, date2) => {
  const d1 = getStringifiedDate(new Date(date1));
  const d2 = getStringifiedDate(new Date(date2));
  return d1 === d2;
};

export const extractISOdate = (date) => {
  const [_date] = date.split("T");
  return _date;
};

export const convert24HourTo12Hour = (time) => {
  const [hour, minute] = time.split(":");
  const hour12 = hour % 12 || 12;
  const ampm = hour < 12 || hour === 24 ? "AM" : "PM";
  return `${hour12}:${minute} ${ampm}`;
};

export const generateDateRangeBetweenToday = (range = 2) => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);
  start.setDate(start.getDate() - range);
  end.setDate(end.getDate() + range);
  return { start, end };
};

export const getDaysBefore = (date, days = 1) => {
  return moment(date).subtract(days, "days").toDate();
};

export const getDaysAfter = (date, days = 1) => {
  return moment(date).add(days, "days").toDate();
};

export const sortArrayOfDates = (dates) => {
  return dates.sort((a, b) => {
    return new Date(a) - new Date(b);
  });
};

export const isNextDay = (date1, date2) => {
  const d1 = new Date(date1);
  const tomorrow = new Date(d1);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const d2 = new Date(date2);
  const isNextDay = tomorrow.toDateString() === d2.toDateString();
  return isNextDay;
};

export const createDateSequence = (datesArray) => {
  const dateSequence = [];
  const sequence = { start: "", end: "" };
  const sortedArray = [...new Set(sortArrayOfDates(datesArray))];
  sortedArray.forEach((date, index) => {
    const nextDate = sortedArray[index + 1];
    // if the next day is exactly the next day of the current date, then continue until the next date is not the next day
    if (nextDate && isNextDay(date, nextDate)) {
      sequence.start = sequence.start || date;
      sequence.end = nextDate;
    } else {
      // if the next day is not the next day of the current date, then push the sequence to the dateSequence array
      if (sequence.start) {
        sequence.end = date;
        dateSequence.push({ ...sequence });
        sequence.start = "";
        sequence.end = "";
      } else {
        dateSequence.push({ start: date, end: date });
      }
    }
  });

  return dateSequence;
};

export const isValidDate = (string) => {
  const date = new Date(string);
  return !isNaN(date);
};

export const selectTimeByDateComparison = (
  startTime,
  endTime,
  firstDate,
  secondDate,
) => {
  if (!firstDate || !secondDate || firstDate !== secondDate) {
    return endTime;
  }

  if (endTime) {
    return startTime > endTime ? startTime : endTime;
  }
};

export const selectTimeByComparison = (startTime, endTime) => {
  return startTime > endTime ? startTime : endTime;
};
